import { computed, inject, Injectable, signal } from '@angular/core';
import { DietQueriesService } from './diet-queries.service';
import { BodyScanSummary, PlanDetail } from '../models/diets.models';

/**
 * Tiny state holder for the logged-in member's own diet plan.
 * - Drawer reads `hasActivePlan()` to decide whether to show the "My diet plan" link.
 * - MyDietPlanComponent reads `plan()` and `scans()` directly.
 * - `loadMyPlan()` fires once per session (or on demand after an admin approval).
 *
 * Kept separate from DietQueriesService so the drawer doesn't pull in every
 * admin-facing query as a dependency.
 */
@Injectable({ providedIn: 'root' })
export class MemberDietService {
  private readonly queries = inject(DietQueriesService);

  readonly plan = signal<PlanDetail | null>(null);
  readonly hasActivePlan = computed(() => this.plan() !== null);
  readonly loaded = signal(false);
  readonly loading = signal(false);

  /** Member's own InBody/Tanita measurements, newest first. Empty when the
   *  trainer hasn't recorded anything yet. Loaded lazily by /my-diet-plan
   *  (drawer doesn't need them). */
  readonly scans = signal<readonly BodyScanSummary[]>([]);
  readonly latestScan = computed(() => this.scans()[0] ?? null);
  readonly previousScan = computed(() => this.scans()[1] ?? null);
  readonly scansLoaded = signal(false);
  readonly scansLoading = signal(false);

  async loadMyPlan(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    try {
      const plan = await this.queries.getMyActivePlan();
      this.plan.set(plan);
      this.loaded.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  /** Pull the member's body-scan history. Cheap (auth.uid()-scoped, capped
   *  at 24 rows), so we don't bother caching aggressively — call from
   *  ngOnInit alongside loadMyPlan. */
  async loadMyScans(): Promise<void> {
    if (this.scansLoading()) return;
    this.scansLoading.set(true);
    try {
      const scans = await this.queries.listMyBodyScans();
      this.scans.set(scans);
      this.scansLoaded.set(true);
    } finally {
      this.scansLoading.set(false);
    }
  }

  /** Call this after admin approval / pwa refresh to pick up plan changes. */
  async refresh(): Promise<void> {
    this.loaded.set(false);
    this.scansLoaded.set(false);
    await Promise.all([this.loadMyPlan(), this.loadMyScans()]);
  }

  clear(): void {
    this.plan.set(null);
    this.loaded.set(false);
    this.scans.set([]);
    this.scansLoaded.set(false);
  }
}
